import { Box, Stack, Typography } from '@mui/material';
import { FC, SyntheticEvent, useCallback, useEffect } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { useAppStore } from '../../app/api/app-api';
import TopBarStore from '../../app/stores/top-bar-store';
import { useAppOwners } from '../../application-owners/api/applications-owners-api';
import AppSpinner from '../../common/components/AppSpinner';
import EmptyPageAvatar from '../../common/components/EmptyPageAvatar';
import { navigate, AppPages } from '../../common/routing/app-navigator';
import SidebarLayout from '../../elements/SidebarLayout/SidebarLayout';
import { loadPipelineSummary } from '../actions/pipeline-summary-actions';
import {
  loadFilters,
  setInitialFilters,
} from '../actions/pipeline-summary-filters-actions';
import PipelineSummaryTable from '../components/PipelineSummaryTable';
import PipelineSummaryFiltersStore from '../store/pipeline-summary-filters-store';
import PipelineSummaryStore from '../store/pipeline-summary-store';
import PipelineSummarySidebarContainer from './PipelineSummarySidebarContainer';

const PipelineSummaryContainer: FC<React.PropsWithChildren<unknown>> = () => {
  const dateRange = useSnapshot(TopBarStore);
  const { displaySelectedAppOwners } = useAppOwners();

  const handleClickConnectors = useCallback(
    (e: SyntheticEvent<HTMLElement>) => {
      e.preventDefault();
      navigate(AppPages.Connectors);
    },
    [],
  );
  const [searchParams] = useSearchParams();
  const { loading, pipelineSummaries } = useSnapshot(PipelineSummaryStore);
  const { loadingFilters } = useSnapshot(PipelineSummaryFiltersStore);
  const { isEmptyOrg } = useAppStore();
  const filters = searchParams.get('filters');

  useEffect(() => {
    setInitialFilters(filters);
    loadPipelineSummary({ update: true });
    loadFilters();
  }, [dateRange, displaySelectedAppOwners, filters]);

  if (
    (loading.isIdle() || loading.isPending() || loadingFilters.isPending()) &&
    !pipelineSummaries
  ) {
    return <AppSpinner text='Loading Pipeline Summary...' />;
  }

  if (loading.isError()) {
    return (
      <Typography variant='body1' color='error'>
        Error loading pipeline summary list, please try again later
      </Typography>
    );
  }

  if (isEmptyOrg) {
    return (
      <EmptyPageAvatar
        text={
          <>
            Please connect your pipeline to OX to see pipeline summary data.
            <br />
            To run a scan...
            <Typography
              variant='body1'
              color='text.primary'
              component='a'
              onClick={handleClickConnectors}
              href={AppPages.Connectors}
              mt={1}
              style={{ display: 'block', fontWeight: 'bold', color: '#FFF' }}>
              Connect your Git projects
            </Typography>
          </>
        }
      />
    );
  }

  return (
    <SidebarLayout aside={<PipelineSummarySidebarContainer />}>
      <Stack direction='column' height='100%'>
        <Stack flex='1 1 auto' sx={{ overflowY: 'auto' }}>
          <Box width='100%' height='100%'>
            {pipelineSummaries && pipelineSummaries.length !== 0 ? (
              <PipelineSummaryTable
                pipelineSummaries={pipelineSummaries}
                loading={loading.isPending() || loadingFilters.isPending()}
              />
            ) : (
              <EmptyPageAvatar
                text={
                  <>
                    <Typography
                      variant='body1'
                      color='text.primary'
                      mt={1}
                      style={{
                        display: 'block',
                        fontWeight: 'bold',
                        color: '#FFF',
                      }}>
                      Beta Users Only
                    </Typography>
                    Can I moootivate you to do more? Attach OX to your pipelines
                    to mooonitor or prevent vulnerabilities from being deployed
                    to production.
                    <Typography
                      variant='body1'
                      color='text.primary'
                      component={NavLink}
                      to={AppPages.Applications}
                      mt={1}
                      style={{
                        display: 'block',
                        fontWeight: 'bold',
                        color: '#FFF',
                      }}>
                      Configure your pipeline settings
                    </Typography>
                  </>
                }
              />
            )}
          </Box>
        </Stack>
      </Stack>
    </SidebarLayout>
  );
};

export default PipelineSummaryContainer;
